import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { type CoachActivityTypeEnum } from "@/modules/coach/coachActivityTypes"
import {
  useCreateProfessionalUser,
  useCurrentCoachIfExists,
  useUpdateCoachSettings,
} from "@/modules/coach/hooks/useProfessionalCoach"
import { CoachCreationAboutStep } from "@/modules/coach/page/steps/coachCreationAboutStep"
import { CoachCreationActivityStep } from "@/modules/coach/page/steps/coachCreationActivityStep"
import { CoachCreationIdentityStep } from "@/modules/coach/page/steps/coachCreationIdentityStep"
import { CoachCreationIntroStep } from "@/modules/coach/page/steps/coachCreationIntroStep"
import { CoachCreationReviewStep } from "@/modules/coach/page/steps/coachCreationReviewStep"
import {
  coachFormDataSchema,
  convertCoachFormDataToInputDto,
  getCoachFormDataDefaultValues,
} from "@/modules/coach/types/coachFormTypes"
import { word } from "@/modules/core/utils/i18n"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { Path } from "@/modules/navigation/routes"
import { useUser } from "@/modules/users/hooks/useUser"
import { LogoHustleUpSmall } from "@images/logoHustleUpSmall"
import { LogoHustleUpText } from "@images/logoHustleUpText"
import { ArrowLeftIcon, ArrowRightIcon, X } from "lucide-react"
import { useEffect, useEffectEvent, useState } from "react"
import { useNavigate } from "react-router"

const STEP_REQUIRED_FIELDS: Record<
  2 | 3 | 4,
  ("commercialName" | "email" | "role" | "yearsOfExperience" | "country" | "description")[]
> = {
  2: ["commercialName", "email"],
  3: ["role", "yearsOfExperience", "country"],
  4: ["description"],
}

type CoachCreationSteps = 1 | 2 | 3 | 4 | 5

const STEPPER_LABELS = [
  "coach.onboarding.stepper.step1",
  "coach.onboarding.stepper.step2",
  "coach.onboarding.stepper.step3",
  "coach.onboarding.stepper.step4",
] as const

export function CoachCreationPage() {
  const navigate = useNavigate()
  const language = useLanguage()
  const { setTheme } = useTheme()
  const user = useUser()
  const { currentCoach: coach } = useCurrentCoachIfExists()
  const { mutateAsync: createCoach, isPending: isCreating } = useCreateProfessionalUser()
  const { mutateAsync: updateCoach, isPending: isUpdating } = useUpdateCoachSettings()

  const isPending = isCreating || isUpdating

  const [step, setStep] = useState<CoachCreationSteps>(1)
  const [selectedActivities, setSelectedActivities] = useState<CoachActivityTypeEnum[]>([])
  const [otherActivities, setOtherActivities] = useState<string[]>([])

  const initActivities = useEffectEvent(() => {
    if (coach) {
      setSelectedActivities(coach.state.activities.filter((a) => a.kind === "predefined").map((a) => a.type))
      setOtherActivities(coach.state.activities.filter((a) => a.kind === "other").map((a) => a.value))
    }
  })

  useEffect(() => {
    initActivities()
  }, [coach])

  const [activityError, setActivityError] = useState<string | null>(null)

  const totalActivities = selectedActivities.length + otherActivities.length

  const applyCoachTheme = useEffectEvent(() => {
    setTheme(Theme.CoachLight)
  })

  useEffect(() => {
    applyCoachTheme()
  }, [])

  const form = useAppForm({
    defaultValues: getCoachFormDataDefaultValues(coach, user),
    validators: {
      onBlur: coachFormDataSchema(language),
      onSubmit: coachFormDataSchema(language),
    },
    onSubmit: async ({ value }) => {
      if (coach) {
        await updateCoach({
          id: coach.state.id,
          payload: convertCoachFormDataToInputDto(value, selectedActivities, otherActivities),
        })
        void navigate(Path.CreateCoachComplete, { state: { coachId: coach.state.id } })
        return
      }
      const created = await createCoach(convertCoachFormDataToInputDto(value, selectedActivities, otherActivities))
      void navigate(Path.CreateCoachComplete, { state: { coachId: created.state.id } })
    },
  })

  const handleClose = () => {
    setTheme(Theme.AthleteLight)
    void navigate(Path.Root)
  }

  const handleBack = () => {
    if (step === 1) {
      void navigate(Path.CoachOnboarding, { replace: true })
      return
    }
    setStep((prev) => (prev - 1) as CoachCreationSteps)
  }

  const handleNext = async () => {
    if (step === 1) {
      setStep(2)
      return
    }

    if (step === 5) {
      void form.handleSubmit()
      return
    }

    const requiredFields = STEP_REQUIRED_FIELDS[step]
    await Promise.all(requiredFields.map(async (f) => form.validateField(f, "submit")))
    const stepHasErrors = requiredFields.some((f) => (form.state.fieldMeta[f]?.errors.length ?? 0) > 0)

    let hasActivityError = false
    if (step === 3) {
      const noActivities = totalActivities === 0
      hasActivityError = noActivities
      setActivityError(noActivities ? word("global.form.required") : null)
    }

    if (stepHasErrors || hasActivityError) return

    setStep((prev) => (prev + 1) as CoachCreationSteps)
  }

  function addOtherActivity() {
    setOtherActivities((prev) => [...prev, ""])
  }

  function updateOtherActivity(index: number, value: string) {
    setOtherActivities((prev) => prev.map((v, i) => (i === index ? value : v)))
  }

  function removeOtherActivity(index: number) {
    setOtherActivities((prev) => prev.filter((_, i) => i !== index))
  }

  function renderStep() {
    switch (step) {
      case 1:
        return <CoachCreationIntroStep form={form} />
      case 2:
        return <CoachCreationIdentityStep form={form} />
      case 3:
        return (
          <CoachCreationActivityStep
            form={form}
            selectedActivities={selectedActivities}
            setSelectedActivities={setSelectedActivities}
            otherActivities={otherActivities}
            addOtherActivity={addOtherActivity}
            updateOtherActivity={updateOtherActivity}
            removeOtherActivity={removeOtherActivity}
            activityError={activityError}
            setActivityError={setActivityError}
          />
        )
      case 4:
        return <CoachCreationAboutStep form={form} />
      case 5:
        return (
          <CoachCreationReviewStep
            form={form}
            selectedActivities={selectedActivities}
            otherActivities={otherActivities}
          />
        )
    }
  }

  return (
    <div className="relative min-h-screen overflow-auto bg-surface-background-secondary">
      <Button variant="outline" size="iconMd" onClick={handleClose} className="absolute top-5 right-5 z-10">
        <X />
      </Button>

      {/* Header: Logo + Stepper */}
      <div className="flex flex-col items-center gap-6 px-12 py-10">
        <div className="flex gap-2">
          <LogoHustleUpSmall color="currentColor" />
          <LogoHustleUpText color="currentColor" />
        </div>
        {step !== 5 && (
          <div className="flex gap-8">
            {STEPPER_LABELS.map((key, index) => {
              const stepNum = (index + 1) as 1 | 2 | 3 | 4
              const isActive = stepNum === step
              const isCompleted = stepNum < step
              return (
                <div key={key} className="flex flex-col items-center gap-1.5">
                  <Text fontStyle="LM" className={cn("text-textOnSurface-default")}>
                    {word(key)}
                  </Text>
                  <div
                    className={cn(
                      "h-4 w-16 rounded-full transition-all",
                      isActive && "bg-interactive-background-primary-faded-highlighted",
                      isCompleted && "bg-interactive-background-primary-faded",
                      !isActive && !isCompleted && "bg-surface-background-default",
                    )}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Content card */}
      <div className="mx-auto mb-12 w-full max-w-[946px] rounded-3xl border border-surface-border-gray-default bg-surface-background-default px-12 py-10">
        {renderStep()}

        {/* Navigation */}
        <div className="mt-10 flex items-center justify-between">
          <Button variant="outline" size="lg" type="button" onClick={handleBack}>
            <ArrowLeftIcon className="size-4" />
            {word("coach.onboarding.nav.previous")}
          </Button>
          <Button variant="primary" size="lg" type="button" onClick={() => void handleNext()} disabled={isPending}>
            {step === 5 ? word("coach.onboarding.review.submit") : word("coach.onboarding.nav.next")}
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
